import type { JSX } from 'react'

import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'

import type { Styles } from '../../theme/types/Styles'

type CommonProps<T extends string> = {
  label?: string
  'aria-label'?: string
  options: T[] | Readonly<T[]>
  getOptionLabel?: (option: T) => string
  error?: boolean
  helperText?: string | null
  required?: boolean
  disabled?: boolean
  fullWidth?: boolean
  size?: 'small' | 'medium'
  variant?: 'outlined' | 'standard' | 'filled'
  sx?: Styles
}

type SingleClearableProps<T extends string> = CommonProps<T> & {
  multiple?: false
  disableClearable?: false
  value: T | null
  onChange: (value: T | null) => void
}

type SingleNonClearableProps<T extends string> = CommonProps<T> & {
  multiple?: false
  disableClearable: true
  value: T
  onChange: (value: T) => void
}

type MultipleClearableProps<T extends string> = CommonProps<T> & {
  multiple: true
  disableClearable?: false
  value: T[] | null
  onChange: (value: T[]) => void
}

type MultipleNonClearableProps<T extends string> = CommonProps<T> & {
  multiple: true
  disableClearable: true
  value: T[]
  onChange: (value: T[]) => void
}

export function SelectString<T extends string>(
  props: SingleClearableProps<T>,
): JSX.Element
export function SelectString<T extends string>(
  props: SingleNonClearableProps<T>,
): JSX.Element
export function SelectString<T extends string>(
  props: MultipleClearableProps<T>,
): JSX.Element
export function SelectString<T extends string>(
  props: MultipleNonClearableProps<T>,
): JSX.Element
export function SelectString<T extends string = string>({
  label,
  'aria-label': ariaLabel,
  value,
  options,
  onChange,
  getOptionLabel,
  error,
  helperText,
  required = false,
  disabled = false,
  disableClearable = false,
  fullWidth = false,
  size = 'small',
  variant = 'standard',
  sx,
  multiple = false,
}:
  | SingleClearableProps<T>
  | SingleNonClearableProps<T>
  | MultipleClearableProps<T>
  | MultipleNonClearableProps<T>) {
  return (
    <Autocomplete
      value={value ?? (multiple ? [] : null)}
      options={options}
      multiple={multiple}
      filterSelectedOptions
      getOptionLabel={getOptionLabel}
      disabled={disabled}
      onChange={(_, newValue) => {
        onChange(newValue as any)
      }}
      disableClearable={disableClearable}
      fullWidth={fullWidth}
      size={size}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          required={required}
          disabled={disabled}
          variant={variant}
          label={label}
          aria-label={ariaLabel}
          error={error}
          helperText={helperText}
        />
      )}
      sx={sx}
    />
  )
}
